import { InternalAssessmentViewRow } from "../../../../internalAssessment/InternalAssessments.types";

export type InternalAssessmentColumnKey =
    | "name"
    | "uniqueIdentifier"
    | "sent"
    | "deadline"
    | "responsibleName"
    | "assigneeName"
    | "questionnaireName"
    | "complianceAreas"
    | "sender"
    | "publicTemplateName"
    | "status"
    | "riskLevel"
    | "score"
    | "assessmentReport"
    | "evaluation"
    | "evaluationName"
    | "reminderSent"
    | "note";

export type InternalAssessmentFilterId =
    | "names"
    | "yearsSent"
    | "sentDateRange"
    | "responsibles"
    | "assignees"
    | "templates"
    | "areas"
    | "senders"
    | "status"
    | "riskLevels"
    | "score"
    | "evaluations";

// Inside a group every assessment shares the group's name, template, deadline, responsible and sender, so those
// columns would repeat the same value on every row. Compliance areas come from the template, so they repeat too -
// the group overview shows them once in its top bar instead.
export const assessmentGroupColumnOrder: Array<InternalAssessmentColumnKey> = [
    "assigneeName",
    "uniqueIdentifier",
    "sent",
    "status",
    "riskLevel",
    "score",
    "evaluation",
    "evaluationName",
    "assessmentReport",
    "reminderSent",
    "note",
];

export const fullColumnOrder: Array<InternalAssessmentColumnKey> = [
    "name",
    "uniqueIdentifier",
    "sent",
    "deadline",
    "responsibleName",
    "assigneeName",
    "questionnaireName",
    "complianceAreas",
    "sender",
    "publicTemplateName",
    "status",
    "riskLevel",
    "score",
    "assessmentReport",
    "evaluation",
    "evaluationName",
    "reminderSent",
    "note",
];

// Mirrors Backend/Core/Filtering/Audits/InternalAssessmentColumns.cs. The hidden evaluationName column only feeds
// search and sorting, so it is never offered in the column picker; the public template is only offered on the tab
// that lists public assessments.
export function getSelectableColumns(showPublicTemplateColumn: boolean | undefined): Array<InternalAssessmentColumnKey> {
    return fullColumnOrder.filter((key) => key !== "evaluationName" && (key !== "publicTemplateName" || !!showPublicTemplateColumn));
}

/**
 * DotLegalTable searches every key of the row object, not only the ones it renders, so inside a group typing a few
 * letters of the shared responsible matches every row and search looks broken. These are the fields the group's
 * column set does not show: the ones behind a dropped column, plus the object-valued ones that can only ever
 * contribute the literal "[object Object]" - they are read through their string counterparts instead.
 */
export const assessmentGroupHiddenSearchFields: Array<keyof InternalAssessmentViewRow> = [
    "name",
    "description",
    "remark",
    "deadline",
    "deadlineString",
    "responsibleName",
    "questionnaireName",
    "sender",
    "publicTemplateName",
    "complianceAreasString",
    "responsible",
    "assignee",
    "assessmentTemplate",
    "complianceAreas",
];

// Two columns here answer to a different filter than the other views give them, for their own reasons.
// Score is a column with no filter at all, matching the external audit group overview (LegalEntityAuditGroupAuditsTab),
// which shows the same column without one: its buckets are fixed quartiles that have nothing to do with the score
// intervals a customer defines on their questionnaire.
// Sent keeps a filter but swaps the year selector for a date range - a group goes out in one batch, so every row
// shares a year and picking one narrows nothing.
const assessmentGroupFilterIds: Array<InternalAssessmentFilterId> = ["sentDateRange", "assignees", "status", "riskLevels", "evaluations"];

const fullFilterIds: Array<InternalAssessmentFilterId> = [
    "names",
    "yearsSent",
    "responsibles",
    "assignees",
    "templates",
    "areas",
    "senders",
    "status",
    "score",
    "evaluations",
];

// Drives the filter menu, the rows the filters are applied to and whether any filter is active. All three have to
// agree: a filter left out of the menu but kept in the matching would go on narrowing the list from session storage
// with no chip and no menu entry to clear it.
export function getFilterIds(withinAssessmentGroup: boolean | undefined): Array<InternalAssessmentFilterId> {
    return withinAssessmentGroup ? assessmentGroupFilterIds : fullFilterIds;
}
